import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { NumpadDecimal } from '../../components/NumpadDecimal';
import { ChevronLeft, Plus, Check, AlertCircle, Trash2, Edit2, Scale, ChevronRight } from 'lucide-react';
import { PRODUCTOS_LISTA_PRECIOS as PRODUCTOS, FACTORES_POLLO } from '../../constants/productos';
import { fmtMoney } from '../../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminListasPrecios() {
  const navigate = useNavigate();
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingLista, setEditingLista] = useState(null);
  
  // Form state
  const [formPeso, setFormPeso] = useState(0);
  const [formPrecios, setFormPrecios] = useState({});
  const [showPesoNumpad, setShowPesoNumpad] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadListas();
  }, []);

  const loadListas = async () => {
    try {
      const { data } = await axios.get(`${API}/listas-precios`);
      // Sort by peso_promedio
      data.sort((a, b) => a.peso_promedio - b.peso_promedio);
      setListas(data);
    } catch (error) {
      setError('Error al cargar listas de precios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormPeso(0);
    setFormPrecios({});
    setEditingLista(null);
    setShowCreate(true);
  };

  const handleEdit = (lista) => {
    setFormPeso(lista.peso_promedio);
    setFormPrecios(lista.precios || {});
    setEditingLista(lista);
    setShowCreate(true);
  };

  const handleDelete = async (listaId) => {
    if (!window.confirm('¿Eliminar esta lista de precios?')) return;
    try {
      await axios.delete(`${API}/listas-precios/${listaId}`);
      await loadListas();
    } catch (error) {
      setError('Error al eliminar lista');
    }
  };

  const handleSave = async () => {
    if (formPeso <= 0) {
      setError('Ingresa el peso promedio');
      return;
    }
    
    setError('');
    setSaving(true);
    
    // Calculate Pollo Entero as sum of all pieces (factored)
    let totalPolloEntero = 0;
    for (const [clave, factor] of Object.entries(FACTORES_POLLO)) {
      totalPolloEntero += (formPrecios[clave] || 0) * factor;
    }
    const preciosConEntero = { ...formPrecios, '8': totalPolloEntero };
    
    const data = {
      nombre: `Pollo ${formPeso} kg`,
      peso_promedio: formPeso,
      precios: preciosConEntero,
    };
    
    try {
      if (editingLista) {
        await axios.put(`${API}/listas-precios/${editingLista.id}`, data);
      } else {
        await axios.post(`${API}/listas-precios`, data);
      }
      setShowCreate(false);
      await loadListas();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar lista');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Create/Edit Form
  if (showCreate) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="crear-lista-page">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreate(false)}
              className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-zinc-900">
              {editingLista ? 'Editar Lista' : 'Nueva Lista de Precios'}
            </h1>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 py-4 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Peso Promedio */}
          <div>
            <label className="block text-zinc-500 text-sm font-medium mb-2 flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Peso Promedio del Pollo (kg)
            </label>
            <button
              onClick={() => setShowPesoNumpad(true)}
              className="w-full h-16 bg-blue-50 border-2 border-blue-200 rounded-xl text-3xl font-bold text-blue-600"
              data-testid="btn-peso"
            >
              {formPeso > 0 ? `${formPeso} kg` : 'Toca para ingresar'}
            </button>
          </div>

          {/* Precios por producto */}
          <div>
            <label className="block text-zinc-500 text-sm font-medium mb-3">
              Precios por Pieza
            </label>
            
            <div className="space-y-2">
              {PRODUCTOS.map((producto) => (
                <div 
                  key={producto.clave} 
                  className="flex items-center justify-between bg-zinc-50 rounded-xl p-3"
                >
                  <span className="font-medium text-zinc-900">{producto.nombre}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500">$</span>
                    <input
                      type="number"
                      value={formPrecios[producto.clave] || ''}
                      onChange={(e) => setFormPrecios({ ...formPrecios, [producto.clave]: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      className="w-24 h-10 px-3 bg-white border border-zinc-300 rounded-lg text-right text-lg font-bold"
                      data-testid={`input-precio-${producto.clave}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pollo Entero (auto-calculated) */}
            {(() => {
              let total = 0;
              for (const [clave, factor] of Object.entries(FACTORES_POLLO)) {
                total += (formPrecios[clave] || 0) * factor;
              }
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-blue-900">Pollo Entero</span>
                      <p className="text-xs text-blue-600">Suma de todas las piezas</p>
                    </div>
                    <span className="text-2xl font-bold text-blue-700" data-testid="precio-pollo-entero">
                      {fmtMoney(total)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-8 pt-4 border-t border-zinc-200">
          <button
            onClick={handleSave}
            disabled={saving || formPeso <= 0}
            className="w-full h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="btn-save-lista"
          >
            {saving ? (
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                {editingLista ? 'Guardar Cambios' : 'Crear Lista'}
              </>
            )}
          </button>
        </div>

        {/* Numpad for Peso */}
        {showPesoNumpad && (
          <NumpadDecimal
            value={formPeso}
            onConfirm={(val) => {
              setFormPeso(val);
              setShowPesoNumpad(false);
            }}
            onCancel={() => setShowPesoNumpad(false)}
            title="Peso Promedio"
            subtitle="Ingresa el peso en kg (ej: 1.8, 2.0, 2.5)"
            maxValue={10}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-listas-precios-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Listas de Precios</h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Add Button */}
        <button
          onClick={handleCreate}
          className="w-full h-14 bg-blue-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 mb-6"
          data-testid="btn-add-lista"
        >
          <Plus className="w-5 h-5" />
          Nueva Lista de Precios
        </button>

        {/* Info */}
        <p className="text-zinc-500 text-sm mb-4">
          Define listas de precios basadas en el peso promedio del pollo. Luego asígnalas a cada punto de venta.
        </p>

        {/* Lists */}
        <div className="space-y-3">
          {listas.length === 0 ? (
            <div className="p-6 bg-zinc-50 rounded-xl text-center text-zinc-500">
              No hay listas de precios. Crea una nueva.
            </div>
          ) : (
            listas.map((lista) => (
              <div 
                key={lista.id} 
                className="bg-zinc-50 rounded-xl p-4"
                data-testid={`lista-${lista.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-zinc-900">{lista.nombre}</h3>
                    <p className="text-zinc-500 flex items-center gap-1">
                      <Scale className="w-4 h-4" />
                      {lista.peso_promedio} kg
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(lista)}
                      className="w-10 h-10 flex items-center justify-center bg-white border border-zinc-300 rounded-lg"
                      data-testid={`btn-edit-${lista.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(lista.id)}
                      className="w-10 h-10 flex items-center justify-center bg-white border border-zinc-300 rounded-lg text-red-500"
                      data-testid={`btn-delete-${lista.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Preview precios */}
                <div className="flex flex-wrap gap-2">
                  {PRODUCTOS.slice(0, 4).map((producto) => {
                    const precio = lista.precios?.[producto.clave] || 0;
                    return (
                      <span key={producto.clave} className="text-sm bg-white px-2 py-1 rounded">
                        {producto.nombre}: {fmtMoney(precio)}
                      </span>
                    );
                  })}
                  {PRODUCTOS.length > 4 && (
                    <span className="text-sm text-zinc-400">+{PRODUCTOS.length - 4} más</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
